import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';

export interface JobDetailsGridProps {
  normalisedRoleLevel?: string | null;
  peopleManagementTier?: string | null;
  jobType?: string | null;
  minimumYearsOfExperience?: number | null;
  visaSponsor?: boolean;
}

export const JobDetailsGrid: React.FC<JobDetailsGridProps> = ({
  normalisedRoleLevel,
  peopleManagementTier,
  jobType,
  minimumYearsOfExperience,
  visaSponsor,
}) => {
  const intl = useIntl();

  if (!normalisedRoleLevel && !peopleManagementTier && !jobType && !minimumYearsOfExperience && !visaSponsor) {
    return null;
  }

  return (
    <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {normalisedRoleLevel && (
        <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.roleLevel" 
              defaultMessage="Role Level" 
              description="Section label for job role level (junior, mid, senior, etc.)" 
            />
          </div>
          <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
            {fixMojibake(normalisedRoleLevel)}
          </div>
        </div>
      )}
      
      {peopleManagementTier && (
        <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.peopleManagementTier" 
              defaultMessage="Management Tier" 
              description="Section label for people management responsibility level" 
            />
          </div>
          <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
            {fixMojibake(peopleManagementTier)}
          </div>
        </div>
      )}
      
      {jobType && (
        <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.jobType" 
              defaultMessage="Job Type" 
              description="Section label for job type (full-time, part-time, contract, etc.)" 
            />
          </div>
          <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
            {fixMojibake(jobType)}
          </div>
        </div>
      )}
      
      {minimumYearsOfExperience != null && minimumYearsOfExperience > 0 && (
        <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.experience" 
              defaultMessage="Experience" 
              description="Section label for required years of experience" 
            />
          </div>
          <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
            <FormattedMessage
              id="jobDetailPage.minimumYearsOfExperience" 
              defaultMessage="{years} {years, plural, one {year} other {years}}"
              description="Text showing minimum years of experience required, where {years} is the number and pluralization handles singular/plural form" 
              values={{ years: minimumYearsOfExperience }} 
            />
          </div>
        </div>
      )}
      
      {visaSponsor && (
        <div className="p-4 rounded-2xl bg-blue-50/60 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-800/60 shadow-sm">
          <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1.5 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.visa" 
              defaultMessage="Visa" 
              description="Section label for visa sponsorship information" 
            />
          </div>
          <div className="text-base font-semibold text-blue-700 dark:text-blue-300">
            <FormattedMessage 
              id="jobDetailPage.visaSponsor" 
              defaultMessage="✓ Sponsorship available"
              description="Message indicating that visa sponsorship is available for this job position" 
            />
          </div>
        </div>
      )}
    </div>
  );
};
